import { computed, inject } from '@angular/core';
import { signalStore, withComputed, withMethods, withState, patchState } from '@ngrx/signals';
import { addEntity, removeEntity, setAllEntities, withEntities } from '@ngrx/signals/entities';

import { v4 as UUIDv4 } from 'uuid';

import { RGBAColor } from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { Constants } from 'app/constants/constants';
import { FilterService } from 'app/modules/collection/services/filter.service';
import { LexDatePipe } from 'app/pipes/lex-date.pipe';
import {
  Dashboard,
  DASHBOARD_LEVEL_WIDGET_GROUP_ID,
  WidgetGroup,
} from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { DEFAULT_COMPARISON_COLOR } from '../services/comparison-handler-service';

interface WidgetGroupExtraState {
  customFields: { fieldName: string; fieldType: string }[];
}

const initialExtraState: WidgetGroupExtraState = {
  customFields: [],
};

/**
 * Signal-based store managing widget group state via NgRx entity management.
 *
 * Provided at the DashboardDetailsComponent level so every child
 * (DashboardViewComponent, WidgetComponent, DashboardPillWrapperComponent, etc.)
 * shares the same instance without prop-drilling via @Input / @Output.
 */
export const WidgetGroupStore = signalStore(
  withState(initialExtraState),

  withEntities<WidgetGroup>(),

  withComputed((state) => ({
    /** Lookup map: widgetId → groupId (includes parentWidget mappings). */
    groupsByWidgetId: computed(() => {
      const map: Record<string, string> = {};
      for (const wg of state.entities()) {
        wg.widgets.forEach((widgetId) => (map[widgetId] = wg.id));
        if (wg.parentWidget) {
          map[wg.parentWidget] = wg.id;
        }
      }
      return map;
    }),

    /** Lookup map: pageId → groupId[] (excludes dashboard-level groups). */
    groupsByPageId: computed(() => {
      const map: Record<number, string[]> = {};
      for (const wg of state.entities()) {
        if (!isDashboardLevelWidgetGroup(wg)) {
          map[wg.page] = map[wg.page] || [];
          map[wg.page].push(wg.id);
        }
      }
      return map;
    }),
  })),

  withMethods(
    (
      store,
      filterService = inject(FilterService),
      accountService = inject(StorageAccountService),
      lexDatePipe = inject(LexDatePipe)
    ) => {
      function isDateCustomField(analyticItem: string): boolean {
        const field = store.customFields().find((cf) => cf.fieldName === analyticItem);
        return !!field && field.fieldType === 'date';
      }

      function translateKeyFor(analyticItem: string, key: string): string | undefined {
        if (!analyticItem) {
          return undefined;
        }
        if (key === '') {
          return Constants.BLANK;
        }
        if (isDateCustomField(analyticItem)) {
          return lexDatePipe.transform(key);
        }
        return filterService.translateKey(analyticItem, key);
      }

      async function translateKeyForMetadataItem(
        analyticItem: string,
        key: string
      ): Promise<{ displayTitle: string; displayValue: string }> {
        await accountService.groupFieldsSub.latest();
        const displayTitle = FieldService.getFieldLongName(analyticItem.replace(/^metadata\./, ''));
        const displayValue = filterService.translateKey(analyticItem, key);
        return { displayTitle, displayValue };
      }

      function addFilterDisplayOptions(filters: any[]): void {
        for (const filter of filters) {
          if (filter.analyticItem.startsWith('metadata.')) {
            translateKeyForMetadataItem(
              filter['analyticItem'],
              filter['displayName'] || filter['value']
            ).then((res) => {
              filter.displayTitle = res.displayTitle;
              filter.displayValue = res.displayValue;
            });
          } else {
            filter.displayValue = translateKeyFor(
              filter['analyticItem'],
              filter['displayName'] || filter['value']
            );
          }
        }
      }

      function cloneJsonValue<T>(value: T | undefined, fallback: T): T {
        return JSON.parse(JSON.stringify(value ?? fallback)) as T;
      }

      function enrichWidgetGroup(widgetGroup: WidgetGroup): WidgetGroup {
        let count = 0;
        let andFilters: any[] = [];
        let orFilters: any[] = [];
        let notFilters: any[] = [];
        for (const key in widgetGroup.filters) {
          if (widgetGroup.filters.hasOwnProperty(key)) {
            count += widgetGroup.filters[key].and.length;
            andFilters = andFilters.concat(widgetGroup.filters[key].and);
            count += widgetGroup.filters[key].or.length;
            orFilters = orFilters.concat(widgetGroup.filters[key].or);
            count += widgetGroup.filters[key].not.length;
            notFilters = notFilters.concat(widgetGroup.filters[key].not);
          }
        }
        for (const key in widgetGroup.parentFilters) {
          if (widgetGroup.filters.hasOwnProperty(key)) {
            count += widgetGroup.parentFilters[key].and.length;
            andFilters = andFilters.concat(widgetGroup.parentFilters[key].and);
          }
        }

        addFilterDisplayOptions(andFilters);
        addFilterDisplayOptions(orFilters);
        addFilterDisplayOptions(notFilters);

        return {
          ...widgetGroup,
          filterCount: count,
          andFilters,
          orFilters,
          notFilters,
        };
      }

      function replaceWidgetGroupEntity(widgetGroup: WidgetGroup): void {
        const enrichedWidgetGroup: WidgetGroup = enrichWidgetGroup(widgetGroup);
        if (store.entityMap()[enrichedWidgetGroup.id]) {
          patchState(store, removeEntity(enrichedWidgetGroup.id));
        }
        patchState(store, addEntity(enrichedWidgetGroup));
      }

      return {
        /** Load all widget groups from a dashboard, replacing existing state. */
        loadWidgetGroups(dashboard: Dashboard): void {
          if (!dashboard?.widgetGroups) {
            patchState(store, setAllEntities<WidgetGroup>([]));
            return;
          }
          const enriched = Object.values(dashboard.widgetGroups).map(enrichWidgetGroup);
          patchState(store, setAllEntities(enriched));
        },

        /** Get a widget group by its ID. */
        getById(id: string): WidgetGroup | undefined {
          return store.entityMap()[id];
        },

        /** Get the widget group associated with a widget (or its parent). */
        getByWidget(widgetId: string): WidgetGroup | undefined {
          const groupId = store.groupsByWidgetId()[widgetId];
          return groupId ? store.entityMap()[groupId] : undefined;
        },

        /** Get widget groups for a given page (including dashboard-level groups), sorted. */
        getByPage(pageId: number): WidgetGroup[] {
          const groupIds = store.groupsByPageId()[pageId] || [];
          return store
            .entities()
            .filter((wg) => groupIds.includes(wg.id) || isDashboardLevelWidgetGroup(wg))
            .sort(widgetGroupSorter);
        },

        /** Register a single widget group into the store. */
        registerWidgetGroup(widgetGroup: WidgetGroup): void {
          replaceWidgetGroupEntity(widgetGroup);
        },

        /** Remove a widget group from the store. */
        unregisterWidgetGroup(widgetGroup: WidgetGroup): void {
          patchState(store, removeEntity(widgetGroup.id));
        },

        /** Get widget IDs used by groups other than the specified one. */
        widgetsInUse(widgetGroupId: string): string[] {
          const usedWidgets: string[] = [];
          const lookup = store.groupsByWidgetId();
          for (const key of Object.keys(lookup)) {
            if (lookup[key] !== widgetGroupId) {
              usedWidgets.push(key);
            }
          }
          return usedWidgets;
        },

        /** Check if the widget belongs to the dashboard-level widget group. */
        hasDashboardLevelWidgetGroupApplied(widgetId: string): boolean {
          const groupId = store.groupsByWidgetId()[widgetId];
          const group = groupId ? store.entityMap()[groupId] : undefined;
          return isDashboardLevelWidgetGroup(group);
        },

        /** Deep-clone a widget group with a new ID. */
        cloneWidgetGroup(widgetGroup: WidgetGroup): WidgetGroup {
          return {
            title: widgetGroup.title,
            color: widgetGroup.color,
            id: UUIDv4(),
            version: 2,
            widgets: [...widgetGroup.widgets],
            filters: cloneJsonValue(widgetGroup.filters, FilterService.getDefaultFilterSet()),
            andFilters: cloneJsonValue(widgetGroup.andFilters, []),
            orFilters: cloneJsonValue(widgetGroup.orFilters, []),
            notFilters: cloneJsonValue(widgetGroup.notFilters, []),
            parentWidget: widgetGroup.parentWidget,
            parentFilters: cloneJsonValue(
              widgetGroup.parentFilters,
              FilterService.getDefaultFilterSet()
            ),
            parameters: cloneJsonValue(widgetGroup.parameters, {}),
            page: widgetGroup.page,
            lastUsedDataSource: widgetGroup.lastUsedDataSource,
            filterCount: widgetGroup.filterCount,
            deletable: widgetGroup.deletable,
            editable: widgetGroup.editable,
            isImpact: widgetGroup.isImpact,
          };
        },

        /** Update a widget group reference on the dashboard object. */
        updateWidgetGroup(dashboard: Dashboard, widgetGroup: WidgetGroup): Dashboard {
          if (Object.getOwnPropertyNames(dashboard.widgetGroups).includes(widgetGroup.id)) {
            const nextWidgetGroup: WidgetGroup = { ...widgetGroup };
            dashboard.widgetGroups[widgetGroup.id] = nextWidgetGroup;
            replaceWidgetGroupEntity(nextWidgetGroup);
          }
          return dashboard;
        },

        /** Set custom field metadata used for filter display translation. */
        setCustomFields(customFields: { fieldName: string; fieldType: string }[]): void {
          patchState(store, { customFields });
        },
      };
    }
  )
);

// ---------------------------------------------------------------------------
// Standalone utility functions (previously static methods on WidgetGroupManager)
// ---------------------------------------------------------------------------

/** Comparator that sorts dashboard-level groups first, then alphabetically. */
export function widgetGroupSorter(wg1: WidgetGroup, wg2: WidgetGroup): number {
  if (isDashboardLevelWidgetGroup(wg1)) {
    return -1;
  }
  if (isDashboardLevelWidgetGroup(wg2)) {
    return 1;
  }
  return wg1.title.localeCompare(wg2.title, undefined, { sensitivity: 'accent' });
}

/** Create a new widget group with the given parameters. */
export function createWidgetGroup(
  name: string,
  widgetsToAdd: string[],
  pageId: number,
  widgetGroupColor?: RGBAColor,
  deletable = true,
  isImpact = false
): WidgetGroup {
  return {
    title: name,
    color: widgetGroupColor,
    id: UUIDv4(),
    version: 2,
    widgets: widgetsToAdd,
    filters: FilterService.getDefaultFilterSet(),
    parentFilters: FilterService.getDefaultFilterSet(),
    parameters: {},
    deletable,
    page: pageId,
    editable: true,
    isImpact,
  };
}

/** Create a dashboard-level widget group. */
export function createDashboardLevelWidgetGroup(name: string): WidgetGroup {
  const widgetGroup = createWidgetGroup(name, [], null, DEFAULT_COMPARISON_COLOR, false);
  widgetGroup.id = DASHBOARD_LEVEL_WIDGET_GROUP_ID;
  widgetGroup.isDashboardLevel = true;
  return widgetGroup;
}

/** Get the dashboard-level widget group from a dashboard. */
export function getDashboardLevelWidgetGroup(dashboard: Dashboard): WidgetGroup | undefined {
  return dashboard.widgetGroups[DASHBOARD_LEVEL_WIDGET_GROUP_ID];
}

/** Check if a widget group is the dashboard-level widget group. */
export function isDashboardLevelWidgetGroup(widgetGroup: WidgetGroup | undefined): boolean {
  return !!widgetGroup?.isDashboardLevel;
}

/** Delete a widget group from a dashboard. */
export function deleteWidgetGroup(dashboard: Dashboard, widgetGroup: WidgetGroup): Dashboard {
  if (Object.getOwnPropertyNames(dashboard.widgetGroups).includes(widgetGroup.id)) {
    delete dashboard.widgetGroups[widgetGroup.id];
  }
  return dashboard;
}
